import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import type { Team } from "@/lib/api/types";
import { getTeamLevelBadgeClass, getTeamLevelLabel } from "@/lib/team-hierarchy";
import { getTeamHueColor } from "@/lib/team-hue";
import { cn } from "@/lib/utils";

interface ChildTeamsTableProps {
  teams?: Team[];
  isLoading?: boolean;
}

function ChildTeamsTableSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03]">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-white/[0.08] text-left text-xs uppercase tracking-[0.2em] text-muted-foreground">
              <th className="px-4 py-3 font-medium">Team</th>
              <th className="px-4 py-3 font-medium">Key</th>
              <th className="px-4 py-3 font-medium">Members</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 4 }).map((_, index) => (
              <tr key={index} className="border-b border-white/[0.05] last:border-b-0">
                <td className="px-4 py-2.5">
                  <div className="h-4 w-40 animate-pulse rounded bg-muted/60" />
                </td>
                <td className="px-4 py-2.5">
                  <div className="h-4 w-16 animate-pulse rounded bg-muted/60" />
                </td>
                <td className="px-4 py-2.5">
                  <div className="h-4 w-10 animate-pulse rounded bg-muted/60" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ChildTeamsTable({ teams = [], isLoading = false }: ChildTeamsTableProps) {
  const navigate = useNavigate();

  if (isLoading) {
    return <ChildTeamsTableSkeleton />;
  }

  if (teams.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-white/[0.08] px-4 py-10 text-center text-sm text-muted-foreground">
        No child teams are linked here yet.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03]">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]" aria-label="Child teams">
          <thead>
            <tr className="border-b border-white/[0.08] text-left text-xs uppercase tracking-[0.2em] text-muted-foreground">
              <th className="px-4 py-3 font-medium">Team</th>
              <th className="px-4 py-3 font-medium">Key</th>
              <th className="px-4 py-3 font-medium">Members</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((team) => {
              const hueColor = getTeamHueColor(team.name);
              const levelLabel = getTeamLevelLabel(team.level);

              return (
                <tr
                  key={team.id}
                  role="link"
                  tabIndex={0}
                  className="cursor-pointer border-b border-white/[0.05] transition-colors hover:bg-white/[0.04] focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary/60 last:border-b-0"
                  onClick={() => navigate(`/teams/${team.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/teams/${team.id}`);
                    }
                  }}
                >
                  <td className="px-4 py-2.5">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: hueColor }} />
                      <span className="truncate text-sm font-medium text-foreground">{team.name}</span>
                      <Badge
                        variant="outline"
                        className={cn("shrink-0 border px-1.5 py-0 text-[10px] font-medium", getTeamLevelBadgeClass(team.level))}
                      >
                        {levelLabel}
                      </Badge>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-muted-foreground">{team.key ?? "—"}</td>
                  <td className="px-4 py-2.5 text-sm text-foreground">{team._count?.memberships ?? 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
